import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.jsx';
import {
  STATUS_LABELS, TICKET_STATUSES, TICKET_PRIORITIES, PRIORITY_LABELS,
  fmtRelative, fmtAbsolute, userLabel
} from '../lib/tickets.js';
import { TicketStatusBadge, TicketPriorityBadge, InitialsAvatar } from '../components/TicketBits.jsx';
import { CollaboratorPicker } from '../components/NewTicketModal.jsx';

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noteBody, setNoteBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState('');
  const [busy, setBusy] = useState(false);
  // Inbound-email attachments (PDFs, images, Office docs) captured by
  // the webhook handler. Loaded as metadata only -- the binary streams
  // via the per-attachment download endpoint when the user clicks.
  const [attachments, setAttachments] = useState([]);

  async function load() {
    setLoading(true);
    try {
      const [t, meta, attRes] = await Promise.all([
        axios.get(`/api/tickets/${id}`),
        axios.get('/api/tickets/meta'),
        axios.get(`/api/tickets/${id}/attachments`)
      ]);
      setTicket(t.data);
      setAttachments(attRes?.data?.attachments || []);
      setUsers(meta.data.users || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function patch(body) {
    setBusy(true);
    try {
      const res = await axios.patch(`/api/tickets/${id}`, body);
      setTicket(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  async function postNote() {
    if (!noteBody.trim()) return;
    setPosting(true);
    try {
      const res = await axios.post(`/api/tickets/${id}/notes`, { body: noteBody.trim() });
      setTicket((t) => ({ ...t, notes: [...(t.notes || []), res.data], updatedAt: new Date().toISOString() }));
      setNoteBody('');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setPosting(false);
    }
  }

  async function removeTicket() {
    const n = ticket.notes?.length || 0;
    if (!window.confirm(`Delete this ticket${n ? ` and its ${n} note(s)` : ''}? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/tickets/${id}`);
      navigate('/tickets');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }

  if (loading) return <section className="page"><p className="modal-sub">Loading…</p></section>;
  if (!ticket) return <section className="page"><p className="error">{error || 'Ticket not found.'}</p></section>;

  const isClosed = ticket.status === 'closed';
  const collabIds = (ticket.collaborators || []).map((c) => c.user.id);

  return (
    <section className="page">
      <div className="page-header">
        <div>
          <Link to="/tickets" className="back-link">← Tickets</Link>
          <h2 className="ticket-detail-title">
            <span className={isClosed ? 'ticket-title-closed' : ''}>{ticket.title}</span>
            <TicketStatusBadge value={ticket.status} />
            {ticket.priority !== 'normal' && <TicketPriorityBadge value={ticket.priority} />}
          </h2>
        </div>
        <button
          type="button"
          className={isClosed ? 'btn-secondary' : 'btn-primary'}
          disabled={busy}
          onClick={() => patch({ status: isClosed ? 'open' : 'closed' })}
        >
          {isClosed ? 'Reopen ticket' : 'Close ticket'}
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="ticket-detail-grid">
        {/* Main column */}
        <div className="ticket-main">
          {ticket.source === 'email' && (
            <div className="card" style={{ background: 'var(--color-surface-hover)' }}>
              <div className="card-head"><span className="card-label">📧 Created from email</span></div>
              <div className="ticket-inbound-meta">
                <span className="card-label">From</span>
                <span className="ticket-side-value">
                  {ticket.inboundFromName ? `${ticket.inboundFromName} <${ticket.inboundFromEmail}>` : ticket.inboundFromEmail}
                </span>
                <span className="card-label">Received</span>
                <span className="ticket-side-value cell-mono">
                  {ticket.inboundReceivedAt ? new Date(ticket.inboundReceivedAt).toLocaleString() : '—'}
                </span>
                {!ticket.contactId && (
                  <>
                    <span className="card-label">Status</span>
                    <span className="ticket-side-value">
                      <span className="badge badge-amber">Unknown sender</span>
                      {' '}— manually link to a customer/lead if appropriate
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
          <div className="card">
            <div className="card-head">
              <span className="card-label">Description</span>
              {!editingDesc && (
                <button type="button" className="btn-link" onClick={() => { setDescDraft(ticket.description); setEditingDesc(true); }}>Edit</button>
              )}
            </div>
            {editingDesc ? (
              <>
                <textarea rows={6} className="ticket-textarea" value={descDraft} onChange={(e) => setDescDraft(e.target.value)} />
                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={() => setEditingDesc(false)} disabled={busy}>Cancel</button>
                  <button type="button" className="btn-primary" disabled={busy || !descDraft.trim()}
                    onClick={async () => { await patch({ description: descDraft.trim() }); setEditingDesc(false); }}>Save</button>
                </div>
              </>
            ) : (
              <pre className="ticket-body-text">{ticket.description}</pre>
            )}
          </div>

          {attachments.length > 0 && (
            <div className="card">
              <div className="card-head"><span className="card-label">Attachments ({attachments.length})</span></div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {attachments.map((a) => {
                  const kb = a.size_bytes ? Math.max(1, Math.round(a.size_bytes / 1024)) : null;
                  return (
                    <li key={a.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <span aria-hidden="true" style={{ color: 'var(--color-text-dim)' }}>{'\u{1F4CE}'}</span>
                      <a
                        href={`/api/tickets/${ticket.id}/attachments/${a.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ flex: 1, color: 'var(--color-accent)' }}
                      >
                        {a.filename}
                      </a>
                      <span className="cell-mono" style={{ color: 'var(--color-text-dim)', fontSize: 'var(--text-small)' }}>
                        {a.content_type || ''}{kb != null ? ` \u2022 ${kb} KB` : ''}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="card">
            <div className="card-head"><span className="card-label">Notes ({ticket.notes?.length || 0})</span></div>
            {(!ticket.notes || ticket.notes.length === 0) ? (
              <p className="empty-tab">No notes yet — add the first one below.</p>
            ) : (
              <ol className="ticket-notes">
                {ticket.notes.map((n) => (
                  <li key={n.id} className="ticket-note">
                    <div className="ticket-note-head">
                      <InitialsAvatar user={n.author} size={20} />
                      <span className="ticket-note-author">{n.author.name || n.author.email}</span>
                      <span className="cell-mono cell-muted">{fmtRelative(n.createdAt)}</span>
                    </div>
                    <pre className="ticket-body-text">{n.body}</pre>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="card">
            <div className="card-head"><span className="card-label">Add note</span></div>
            <textarea
              rows={3} className="ticket-textarea"
              placeholder="Add an update — what you tried, what you heard back, next step…"
              value={noteBody} onChange={(e) => setNoteBody(e.target.value)}
            />
            <div className="form-actions">
              <button type="button" className="btn-primary" disabled={posting || !noteBody.trim()} onClick={postNote}>
                {posting ? 'Posting…' : 'Post note'}
              </button>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-danger" onClick={removeTicket}>Delete ticket</button>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="ticket-sidebar">
          {ticket.siteName && (
            <div className="card">
              <span className="card-label">Site</span>
              {ticket.siteId
                ? <Link to={`/customers`} className="ticket-side-value">{ticket.siteName}</Link>
                : <span className="ticket-side-value">{ticket.siteName}</span>}
            </div>
          )}
          {ticket.siteXsiteIoUser && (
            <div className="card">
              <span className="card-label">XSite IO User</span>
              <span className="ticket-side-value cell-mono">{ticket.siteXsiteIoUser}</span>
            </div>
          )}
          <div className="card">
            <span className="card-label">Status</span>
            <select className="side-select" value={ticket.status} disabled={busy}
              onChange={(e) => patch({ status: e.target.value })}>
              {TICKET_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </div>
          <div className="card">
            <span className="card-label">Primary owner</span>
            <select className="side-select" value={ticket.primaryAssigneeId ?? ''} disabled={busy}
              onChange={(e) => patch({ primaryAssigneeId: e.target.value || null })}>
              <option value="">Unassigned</option>
              {users.map((u) => <option key={u.id} value={u.id}>{userLabel(u, user?.id)}</option>)}
            </select>
          </div>
          <div className="card">
            <span className="card-label">Collaborators</span>
            <CollaboratorPicker
              users={users}
              selectedIds={collabIds}
              excludeUserId={ticket.primaryAssigneeId}
              currentUserId={user?.id}
              onAdd={(uid) => patch({ addCollaboratorIds: [uid] })}
              onRemove={(uid) => patch({ removeCollaboratorIds: [uid] })}
            />
          </div>
          <div className="card">
            <span className="card-label">Priority</span>
            <select className="side-select" value={ticket.priority} disabled={busy}
              onChange={(e) => patch({ priority: e.target.value })}>
              {TICKET_PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
            </select>
          </div>
          <div className="card">
            <span className="card-label">Created by</span>
            <div className="ticket-side-meta">
              <InitialsAvatar user={ticket.createdBy} size={20} />
              <span>{ticket.createdBy?.name || ticket.createdBy?.email}</span>
            </div>
            <span className="cell-mono cell-muted">{fmtAbsolute(ticket.createdAt)}</span>
          </div>
          <div className="card">
            <span className="card-label">Last updated</span>
            <span className="cell-mono cell-muted">{fmtRelative(ticket.updatedAt)} · {fmtAbsolute(ticket.updatedAt)}</span>
          </div>
          {ticket.closedAt && (
            <div className="card">
              <span className="card-label">Closed at</span>
              <span className="cell-mono cell-muted">{fmtAbsolute(ticket.closedAt)}</span>
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}
